import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';

// core components
import Card from '../md/components/Card/Card.jsx';
import CardHeader from '../md/components/Card/CardHeader.jsx';
import CardText from '../md/components/Card/CardText.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';

import { addPerson } from '../store/slices/personsSlice';
import { addOrUpdateContacts } from '../store/slices/emergencyContactsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch, validatePhoneNumber, validateEmail, showAlert } from '../utils/utils';

import ReduxLoader from '../store/ReduxLoader';

import regularFormsStyle from '../assets/jss/crm/views/regularFormsStyle';

import CrmTextField from './CrmTextField';
import CrmButton from './CrmButton';
import PhoneInput from './PhoneInput';
import AlertModal from './AlertModal';

const registerAccountFormStyles = (theme) => ({
  ...regularFormsStyle,
  container: {
    minWidth: '500px',
  },
  contactInfoHeader: {
    display: 'inline-block',
    marginTop: '40px',
  },
  textFieldHolder: {
    marginTop: '16px',
  },
  contactPhoneInputDiv: {
    marginTop: '14px',
    marginBottom: '12px',
  },
});

/*
Call this with
    <NewClientContactForm
        viewingFirmId={firmId}
        clientId={personId}
        relationship="<some (possibly empty) string>"
        successFunc={<(newPersonId, relationship) => a function(newPersonId, relationship) to take care of newPersonId and close the dialog>}
        closeModalFunc={a function for simply closing the dialog}
    />
*/

class NewClientContactForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      redirectToFirmOverview: false,
      redirectToAccounts: false,
      redirectToLogin: false,

      contactName: '',
      contactPhoneRoles: ['Mobile'],
      contactPhoneNumber: '',
      contactEmail: '',
      relationship: '',
      contactDescription: '',
    };
  }

  validationPolicy = {
    requireRelationship: true,
    requireContactDescription: false,
    requireAtLeastOneOfContactPhoneOrEmail: true,
    requireContactPhone: false,
    requireContactEmail: false,
  };

  allPossiblePhoneRoles = ['Mobile', 'Business', 'Home', 'Work', 'Fax'];
  possibleContactPhoneRoles = ['Work', 'Mobile', 'Home'];

  componentDidMount() {
    this.setState({ relationship: this.props.relationship });
  }

  handleTextChange(event) {
    this.setState({ [event.target.id]: event.target.value });
  }

  constructPhoneRolesArray(rolesObj) {
    let phoneRoles = [];
    this.allPossiblePhoneRoles.forEach((roleName) => {
      if (rolesObj[roleName]) {
        phoneRoles = phoneRoles.concat(roleName);
      }
    });
    return phoneRoles;
  }

  handleContactPhoneRolesChange(rolesObj) {
    const newPhoneRoles = this.constructPhoneRolesArray(rolesObj);
    this.setState({ contactPhoneRoles: newPhoneRoles });
  }

  handleContactPhoneNumberChange(newNumber) {
    this.setState({ contactPhoneNumber: newNumber });
  }

  validateForm() {
    let validation = this.validateContactName();
    if (!validation.valid) {
      return validation;
    }

    if (this.validationPolicy.requireRelationship) {
      validation = this.validateRelationship();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireContactDescription) {
      validation = this.validateContactDescription();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireContactPhone) {
      validation = this.validateContactPhone();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireContactEmail) {
      validation = this.validateContactEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireAtLeastOneOfContactPhoneOrEmail) {
      validation = this.validateContactPhoneOrEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    return { valid: true };
  }

  validateContactName() {
    const name = this.state.contactName.trim();
    if (name.length < 5) {
      return {
        valid: false,
        errorMessage: 'You need to provide a longer Name for the Contact',
      };
    }
    const firstSpaceLoc = name.indexOf(' ');
    if (firstSpaceLoc < 0) {
      return {
        valid: false,
        errorMessage: 'The Name for the Contact must have a space in the middle of it somewhere',
      };
    }
    return {
      valid: true,
    };
  }

  validateRelationship() {
    if (this.state.relationship.length === 0) {
      return { valid: false, errorMessage: 'You must supply a Relationship for the Contact' };
    } else {
      return { valid: true };
    }
  }

  validateContactDescription() {
    if (this.state.contactDescription.length === 0) {
      return { valid: false, errorMessage: 'You must supply a Description for the Contact' };
    } else {
      return { valid: true };
    }
  }

  validateContactPhone() {
    return this.state.contactPhoneNumber
      ? this.validatePhoneNumberAndRoles(
          this.state.contactPhoneNumber,
          this.state.contactPhoneRoles,
          'Primary Contact Phone'
        )
      : { valid: false, errorMessage: 'You must supply a phone number for the Primary Contact' };
  }

  validateContactEmail() {
    return this.state.contactEmail
      ? validateEmail(this.state.contactEmail, 'Primary Contact Email does not appear to be in a valid format')
      : { valid: false, errorMessage: 'You must supply an email address for the Primary Contact' };
  }

  validatePhoneNumberAndRoles(number, roles, label) {
    if (number) {
      const validation = validatePhoneNumber(number, label);
      if (!validation.valid) {
        return validation;
      } else {
        return this.validateContactPhoneAndRolesConsistency(number, roles, label);
      }
    } else {
      return this.validateContactPhoneAndRolesConsistency(number, roles, label);
    }
  }

  validateContactPhoneAndRolesConsistency(number, roles, label) {
    if (number) {
      if (roles) {
        return { valid: true };
      } else {
        return {
          valid: false,
          errorMessage: 'For ' + label + ', since you have supplied a number, you must also check a role',
        };
      }
    } else {
      if (roles) {
        return {
          valid: false,
          errorMessage: 'For ' + label + ', since you have  checked a role, you must also supply a number',
        };
      } else {
        return { valid: true };
      }
    }
  }

  validateContactPhoneOrEmail() {
    if (this.state.contactEmail || this.state.contactPhoneNumber) {
      if (this.state.contactPhoneNumber) {
        const validation = this.validateContactPhone();
        if (!validation.valid) {
          return validation;
        }
      }
      if (this.state.contactEmail) {
        const validation = validateEmail(
          this.state.contactEmail,
          "The supplied Primary Contact Email doesn't appear to be valid"
        );
        if (!validation.valid) {
          return validation;
        }
      }
      return { valid: true };
    } else {
      return {
        valid: false,
        errorMessage: 'You must supply (at least) either a phone number or an email address for the Primary Contact',
      };
    }
  }

  clearForm() {
    const freshState = {
      contactName: '',
      contactEmail: '',
      contactPhoneNumber: '',
      relationship: '',
      contactDescription: '',
    };
    this.setState(freshState);
  }

  handleSubmit = () => {
    this.setState({ errorMessage: '' });

    const validation = this.validateForm();
    if (!validation.valid) {
      showAlert(this, validation.errorMessage);
      return;
    }

    this.createAndStorePersonAndContactRecords();
  };

  createAndStorePersonAndContactRecords() {
    // First create and store the Person record
    const { viewingFirmId, clientId, successFunc } = this.props;
    const personUri = '/api/person';
    const personApiStruct = this.createPersonApiStruct();
    const personRequest = createStdRequest(personUri, 'POST', personApiStruct);
    const actionOnGoodCreatePersonResponse = (json) => {
      // store this person in Redux
      const reduxStruct = ReduxLoader.createReduxStructForFetchedPersonOrContactApiStruct(json);
      this.props.addPerson(reduxStruct);

      // Now create and store a ClientContact record
      const contactId = json.personId;
      const clientUri = `/api/person/${clientId}/contact`;
      const clientContactApiStruct = this.createClientContactApiStruct(viewingFirmId, clientId, contactId);
      const clientContactRequest = createStdRequest(clientUri, 'POST', clientContactApiStruct);
      const actionOnGoodClientContactResponse = (json) => {
        // store whatever we need to in Redux
        const clientContactRecord = json.clientContact;
        this.addNewContactToReduxStore(clientId, clientContactRecord);

        this.clearForm();
        successFunc(contactId, clientContactApiStruct.relationship);
      };
      apiFetch(this, clientContactRequest, actionOnGoodClientContactResponse);
    };
    apiFetch(this, personRequest, actionOnGoodCreatePersonResponse);
  }

  addNewContactToReduxStore(clientId, contactApiStruct) {
    const reduxStruct = ReduxLoader.convertContactsApiStructIntoReduxStruct([contactApiStruct]);
    this.props.addOrUpdateContacts({ clientId: clientId, contacts: reduxStruct });
  }

  createPersonApiStruct() {
    const personId = 0;
    const email = this.state.contactEmail.trim();
    const nameStruct = this.parseContactName();
    const { firstName, middleNames, lastName } = nameStruct;
    const preferredName = firstName;
    const sex = 'Unknown';
    let phoneNumbers = [];
    if (this.state.contactPhoneNumber) {
      const phoneRec = {
        roles: this.state.contactPhoneRoles.join(','),
        countryCode: '1',
        number: this.state.contactPhoneNumber,
      };
      phoneNumbers = [phoneRec];
    }
    return {
      personId,
      email,
      firstName,
      middleNames,
      lastName,
      preferredName,
      sex,
      // birthDate,
      phoneNumbers,
      // addresses,
    };
  }

  createClientContactApiStruct(viewingFirmId, clientId, contactId) {
    return {
      clientContactId: 0,
      clientId,
      contactId,
      relationship: this.state.relationship.trim(),
      description: this.state.contactDescription.trim(),
      viewingFirmId,
    };
  }

  parseContactName() {
    const name = this.state.contactName;
    const trimmedName = name.trim();
    const firstSpaceLoc = trimmedName.indexOf(' ');
    const lastSpaceLoc = trimmedName.lastIndexOf(' ');
    const firstName = trimmedName.substring(0, firstSpaceLoc);
    const lastName = trimmedName.substring(lastSpaceLoc + 1);
    const middleNames =
      lastSpaceLoc > firstSpaceLoc ? trimmedName.substring(firstSpaceLoc + 1, lastSpaceLoc).trim() : '';
    return { firstName, middleNames, lastName };
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    if (this.state.redirectToLogin) {
      return <Redirect to="login" push />;
    }

    if (this.state.redirectToFirmOverview) {
      return <Redirect to="firmOverview" push />;
    }

    if (this.state.redirectToAccounts) {
      return <Redirect to="accounts" push />;
    }

    const { classes } = this.props;

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <Grid container className={classes.container}>
          <Grid item xs={12}>
            <Card>
              <CardHeader color="allevaOrange" text>
                <CardText color="allevaOrange">
                  <h4 className={classes.cardTitle}>Create Contact</h4>
                </CardText>
              </CardHeader>
              <CardBody>
                <Grid container>
                  <Grid item xs={12}>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        required
                        label="Name"
                        id="contactName"
                        fullWidth
                        value={this.state.contactName}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Relationship"
                        id="relationship"
                        fullWidth
                        value={this.state.relationship}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Description"
                        id="contactDescription"
                        fullWidth
                        value={this.state.contactDescription}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        required
                        label="Email Address"
                        id="contactEmail"
                        fullWidth
                        value={this.state.contactEmail}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.contactPhoneInputDiv}>
                      <PhoneInput
                        label="Contact Phone"
                        possibleRoles={this.possibleContactPhoneRoles}
                        actualRoles={this.state.contactPhoneRoles}
                        number={this.state.contactPhoneNumber}
                        onRolesChange={(rolesObj) => this.handleContactPhoneRolesChange(rolesObj)}
                        onNumberChange={(newNumber) => this.handleContactPhoneNumberChange(newNumber)}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Grid container justifyContent="flex-end">
                  <Grid item xs={12} sm={12} md={12}>
                    <CrmButton filled onClick={() => this.handleSubmit()}>
                      Create Contact
                    </CrmButton>
                  </Grid>
                </Grid>
              </CardBody>
            </Card>
          </Grid>
        </Grid>
      </div>
    );
  }
}

const actionProducers = {
  setApiResponseInfo,
  addPerson,
  addOrUpdateContacts,
};

export default connect(null, actionProducers)(withStyles(registerAccountFormStyles)(NewClientContactForm));
